import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useShortcutContext from '@/hooks/useShortcutContext';
import './Modal.css';

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  contextId = null, // Optional custom context ID for shortcuts
}) {
  // Generate unique ID for this modal instance
  const autoId = useId();
  const modalContextId = contextId || `modal:${autoId}`;

  // Register shortcut context for this modal (only when open and closeOnEscape is true)
  useShortcutContext(
    modalContextId,
    { cancel: onClose },
    isOpen && closeOnEscape
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.cursor = ''; // Reset any lingering cursor styles
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.cursor = ''; // Ensure cursor is reset on close
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.cursor = ''; // Cleanup on unmount
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className={`modal-content ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {title && (
          <div className="modal-header">
            <h3 id="modal-title" className="modal-title">{title}</h3>
            {showCloseButton && (
              <button
                className="button-square"
                onClick={onClose}
                aria-label="Close modal"
                data-sfx="button"
              >
                <X />
              </button>
            )}
          </div>
        )}

        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
